import { useState, useRef, useEffect } from 'react';
import { User } from '../types/User';

/**
 * 받아 둔 길드원 목록을 모듈에 남긴다.
 *
 * 탭을 옮기면 화면이 통째로 다시 만들어져서, 그때마다 다시 받으면 들어올 때마다
 * "불러오는 중"이 뜬다. 남겨 둔 값이 있으면 먼저 그리고 뒤에서 조용히 갱신한다.
 */
let usersCache: User[] | null = null;

export function useGuildMembers() {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // 직급별 우선순위 정의 (숫자가 낮을수록 높은 직급)
  const getRolePriority = (role: string) => {
    switch (role) {
      case 'master': return 1;
      case 'submaster': return 2;
      case 'senior': return 3;
      case 'member': return 4;
      default: return 5;
    }
  };

  // 정렬된 사용자 목록 반환
  /**
   * **명단에 없는 계정은 길드원 목록에서 뺀다.**
   *
   * 게임 명단 29명인데 사이트 계정이 31명이었다 — 게임 닉과 다른 이름으로 가입했거나
   * 길드를 나갔는데 계정이 남은 사람들이다. 목록에 섞이면 인원 수가 게임과 달라진다.
   * 대신 화면 아래에 따로 알려 준다(`outsiders`).
   */
  const insiders = users.filter((u) => u.inGuild !== false);
  const outsiders = users.filter((u) => u.inGuild === false);

  const sortedUsers = insiders.sort((a, b) => {
    const priorityA = getRolePriority(a.role);
    const priorityB = getRolePriority(b.role);
    
    // 직급이 다르면 직급순으로 정렬
    if (priorityA !== priorityB) {
      return priorityA - priorityB;
    }
    
    // 같은 직급이면 닉네임 순으로 정렬
    return a.nickname.localeCompare(b.nickname);
  });
  const [newUserId, setNewUserId] = useState('');
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState('');
  const [editNick, setEditNick] = useState<{ userId: string, value: string } | null>(null);
  const [nickSaving, setNickSaving] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState<string | null>(null);
  const [joinRequests, setJoinRequests] = useState<any[]>([]);
  const [joinLoading, setJoinLoading] = useState(false);
  const [joinError, setJoinError] = useState('');
  const [myRole, setMyRole] = useState<'master' | 'submaster' | 'senior' | 'member' | null>(null);
  /** 사이트 운영자인지. 직급은 인게임 표시값이라 권한은 이 값으로 판단한다 */
  const [mySiteAdmin, setMySiteAdmin] = useState(false);
  const [myUuid, setMyUuid] = useState<string | null>(null);
  const [roleEditId, setRoleEditId] = useState<string | null>(null);
  const roleDropdownRef = useRef<HTMLDivElement>(null);
  const roleSelectRefs = useRef<{ [userId: string]: HTMLSelectElement | null }>({});
  const [popupOpen, setPopupOpen] = useState(false);

  const fetchUsers = async () => {
    if (usersCache) setUsers(usersCache);
    setLoading(!usersCache);
    setError('');
    try {
      const res = await fetch('/api/auth/users', { credentials: 'include' });
      const data = await res.json();
      if (data.users) {
        usersCache = data.users;
        setUsers(data.users);
      } else {
        setError(data.error || '불러오기 실패');
      }
    } catch {
      setError('서버 오류');
    } finally {
      setLoading(false);
    }
  };

  const fetchJoinRequests = async () => {
    setJoinLoading(true);
    setJoinError('');
    try {
      const res = await fetch('/api/auth/join-requests', { credentials: 'include' });
      const data = await res.json();
      if (data.requests) {
        setJoinRequests(data.requests);
      } else {
        setJoinError(data.error || '불러오기 실패');
      }
    } catch {
      setJoinError('서버 오류');
    } finally {
      setJoinLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchJoinRequests();
    (async () => {
      try {
        const res = await fetch('/api/auth/me', { credentials: 'include' });
        const data = await res.json();
        if (data.uuid && data.role) {
          setMyUuid(data.uuid);
          setMyRole(data.role);
          setMySiteAdmin(!!data.siteAdmin);
        }
      } catch {}
    })();
  }, []);

  // 가입 승인
  const handleApprove = async (userId: string) => {
    if (!window.confirm('이 가입 요청을 승인하시겠습니까?')) return;
    setJoinLoading(true);
    try {
      const res = await fetch('/api/auth/join-approve', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ userId })
      });
      const data = await res.json();
      if (data.success) {
        fetchUsers();
        fetchJoinRequests();
      } else {
        alert(data.error || '승인 실패');
      }
    } catch {
      alert('서버 오류');
    } finally {
      setJoinLoading(false);
    }
  };

  // 가입 거절
  const handleReject = async (userId: string) => {
    if (!window.confirm('이 가입 요청을 거절하시겠습니까?')) return;
    setJoinLoading(true);
    try {
      const res = await fetch('/api/auth/join-reject', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ userId })
      });
      const data = await res.json();
      if (data.success) {
        fetchJoinRequests();
      } else {
        alert(data.error || '거절 실패');
      }
    } catch {
      alert('서버 오류');
    } finally {
      setJoinLoading(false);
    }
  };

  /**
   * 직급 변경 기능은 걷어냈다 — 직급은 게임 등급을 따라 자동 동기화되므로
   * 사이트에서 바꿔도 다음 명단 동기화에 덮어써진다. 화면은 배지로 보여만 준다.
   */

  // 길드원 추가
  const handleAddUser = async () => {
    if (!newUserId.trim()) return;
    setAdding(true);
    setAddError('');
    try {
      const res = await fetch('/api/auth/add-user', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ userId: newUserId.trim() })
      });
      const data = await res.json();
      if (data.success) {
        setNewUserId('');
        fetchUsers();
      } else {
        setAddError(data.error || '추가 실패');
      }
    } catch {
      setAddError('서버 오류');
    } finally {
      setAdding(false);
    }
  };

  /**
   * 닉네임·프로필 이미지 변경 기능은 걷어냈다.
   *
   * 닉은 인게임 닉을 자동으로 따라가고(명단 동기화가 맞춘다), 얼굴은 게임 화면에서
   * 잘라 올린다. 사이트에서 따로 바꿀 수 있으면 동기화 때 덮어써져 뭐가 맞는지 알 수 없다.
   */
  // 길드원 삭제
  const handleDeleteUser = async (uuid: string) => {
    if (!window.confirm('정말 삭제하시겠습니까?')) return;
    setDeleteLoading(uuid);
    try {
      const res = await fetch('/api/auth/user', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ uuid })
      });
      const data = await res.json();
      if (data.success) {
        fetchUsers();
      } else {
        alert(data.error || '삭제 실패');
      }
    } catch {
      alert('서버 오류');
    } finally {
      setDeleteLoading(null);
    }
  };

  return {
    users: sortedUsers, outsiders, setUsers, loading, setLoading, error, setError, newUserId, setNewUserId, adding, setAdding, addError, setAddError,
    editNick, setEditNick, nickSaving, setNickSaving, deleteLoading, setDeleteLoading, joinRequests, setJoinRequests,
    joinLoading, setJoinLoading, joinError, setJoinError, myRole, setMyRole, mySiteAdmin, setMySiteAdmin, myUuid, setMyUuid, roleEditId, setRoleEditId,
    roleDropdownRef, roleSelectRefs, popupOpen, setPopupOpen,
    fetchUsers, fetchJoinRequests,
    handleApprove, handleReject,
    handleAddUser, handleDeleteUser,
    getRolePriority,
  };
} 